
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Lead, LeadComment, User, Appointment, AppointmentType, UserRole, LeadActivity, Opportunity, Task, LeadSheetConfig, LeadSheetTemplate, Company } from '../types';
import { api } from '../services/apiService';
import Timeline from './Timeline';
import SelectField from './SelectField';
import OpportunityPDFModal from './OpportunityPDFModal';
import { displayEmail, displayPhone } from '../utils/maskContact';
import { toast } from '../services/toast';
import {
  renderEmailTemplate,
  buildEmailTemplateContext,
  findUnresolvedEmailPlaceholders,
  insertVariableAtCursor,
  EMAIL_TEMPLATE_VARIABLES,
  emailVariableGroupLabel,
} from '../services/emailTemplateRender';
import { CustomFieldDefinition, StatusDefinition } from '../types';
import { DEFAULT_STATUSES } from '../constants';

const _sheetTemplateCache = new Map<string, LeadSheetTemplate | null>();

// Permet aux écrans d'admin (paramètres > fiche prospect) d'invalider le cache
// après modification d'un template, sans avoir à recharger la page complète.
export function invalidateLeadSheetTemplateCache(): void {
  _sheetTemplateCache.clear();
}

interface LeadDetailsModalProps {
  lead: Lead;
  currentUser: User;
  companyUsers: User[];
  /** Tenant courant — utilisé pour résoudre les variables {{company.*}} dans les templates email. */
  company?: Company;
  currency?: string;
  timezone?: string;
  revenueBrackets?: string[];
  maskContacts?: boolean;
  customFieldDefinitions?: CustomFieldDefinition[];
  customStatuses?: StatusDefinition[];
  onClose: () => void;
  onStatusChange: (newStatus: Lead['status']) => void;
  onUpdate: (updatedLead: Lead) => void;
  onDelete?: (leadId: string) => void | Promise<void>;
  /** Mode création : ouvre la fiche entièrement vierge, désactive les actions
   *  liées à un lead existant (commentaires, RDV, ERP, favoris, suppression) et
   *  remplace `onUpdate` par `onCreate` au moment du save. */
  isCreating?: boolean;
  onCreate?: (data: Omit<Lead, 'id' | 'createdAt'>) => Promise<void> | void;
}

const getSymbol = (code?: string) => {
    switch(code) {
        case 'USD': return '$';
        case 'GBP': return '£';
        case 'CHF': return 'CHF ';
        default: return '€';
    }
};

const DEFAULT_REVENUE_BRACKETS = ['<5K', '5-20K', '20-50K', '50-100K', '>100K'];

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({ lead, currentUser, companyUsers, company, currency = 'EUR', timezone, revenueBrackets: propBrackets, maskContacts, customFieldDefinitions = [], customStatuses, onClose, onUpdate, onDelete, isCreating = false, onCreate }) => {
  const effectiveBrackets = (propBrackets && propBrackets.length > 0) ? propBrackets : DEFAULT_REVENUE_BRACKETS;
  // États de données
  const [comments, setComments] = useState<LeadComment[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [pdfOpportunity, setPdfOpportunity] = useState<Opportunity | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [availableSectors, setAvailableSectors] = useState<string[]>([]);

  // ERP push state
  const [erpPushState, setErpPushState] = useState<'idle' | 'pushing' | 'done' | 'already' | 'error'>('idle');
  const [erpCustomerId, setErpCustomerId] = useState<string | null>(lead.erpCustomerId || null);

  // ERP documents state
  const [erpDocs, setErpDocs] = useState<{linked: boolean; quotes: any[]; invoices: any[]; payments: any[]} | null>(null);
  const [erpDocsLoading, setErpDocsLoading] = useState(false);
  
  // États de formulaire Note/Email
  const [newComment, setNewComment] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [emailLeadFiles, setEmailLeadFiles] = useState<string[]>([]); // IDs des fichiers du lead à joindre
  const [showLeadFilePicker, setShowLeadFilePicker] = useState(false);
  const emailFileRef = React.useRef<HTMLInputElement>(null);
  const emailSubjectRef = React.useRef<HTMLInputElement>(null);
  const emailBodyRef = React.useRef<HTMLTextAreaElement>(null);
  const [emailActiveField, setEmailActiveField] = useState<'subject' | 'body'>('body');
  const [showVariablePalette, setShowVariablePalette] = useState(false);

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState<Array<{ id: string; name: string; subject: string; body: string; created_at: string }>>([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  
  // États UI - 4 onglets : commentaires (défaut), email, timeline, opportunités
  const [activeTab, setActiveTab] = useState<'comments' | 'email' | 'timeline' | 'opportunities' | 'tasks' | 'files' | 'info'>('comments');

  // Modèle de fiche prospect appliqué à l'utilisateur courant (null = tout visible)
  // Cache module-level pour éviter le flash de réorganisation à chaque ouverture
  const [sheetTemplate, setSheetTemplate] = useState<LeadSheetTemplate | null>(
    () => _sheetTemplateCache.get(currentUser.id) ?? null
  );
  const [sheetTemplateReady, setSheetTemplateReady] = useState(
    () => _sheetTemplateCache.has(currentUser.id)
  );
  useEffect(() => {
    if (_sheetTemplateCache.has(currentUser.id)) {
      setSheetTemplate(_sheetTemplateCache.get(currentUser.id) ?? null);
      setSheetTemplateReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const tpl = await api.getMyLeadSheetTemplate();
        if (!cancelled) {
          _sheetTemplateCache.set(currentUser.id, tpl);
          setSheetTemplate(tpl);
        }
      } finally {
        if (!cancelled) setSheetTemplateReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser.id]);
  const sheetCfg: LeadSheetConfig = sheetTemplate?.config || {};
  const showTab    = (k: keyof NonNullable<LeadSheetConfig['tabs']>)    => sheetCfg.tabs?.[k] !== false;
  const showAction = (k: keyof NonNullable<LeadSheetConfig['actions']>) => sheetCfg.actions?.[k] !== false;
  const showField  = (k: keyof NonNullable<LeadSheetConfig['fields']>)  => sheetCfg.fields?.[k] !== false;
  // Si l'onglet actif est masqué par le template, basculer vers un onglet visible
  useEffect(() => {
    const map: Record<string, keyof NonNullable<LeadSheetConfig['tabs']>> = {
      comments: 'comments', email: 'email', timeline: 'timeline', opportunities: 'opportunities', tasks: 'tasks', files: 'files'
    };
    const current = map[activeTab as string];
    if (current && !showTab(current)) {
      const fallback = (['comments','email','timeline','opportunities','tasks','files'] as const).find(t => showTab(t as any));
      if (fallback) setActiveTab(fallback as any);
    }
  }, [sheetTemplate, activeTab]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oppSubject, setOppSubject] = useState('');
  const [oppComment, setOppComment] = useState('');
  const [oppItems, setOppItems] = useState<Array<{ label: string; quantity: number; unitPrice: number }>>([
    { label: '', quantity: 1, unitPrice: 0 }
  ]);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskComment, setTaskComment] = useState('');
  const [taskDueAt, setTaskDueAt] = useState(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
  
  const [confirmDeleteLead, setConfirmDeleteLead] = useState(false);

  // Call logging
  const [showCallLog, setShowCallLog] = useState(false);
  const [callDuration, setCallDuration] = useState('');
  const [callDirection, setCallDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [callNotes, setCallNotes] = useState('');
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [callLogsLoaded, setCallLogsLoaded] = useState(false);

  // Attachments
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // États Lead Local
  const [localAdditionalInfo, setLocalAdditionalInfo] = useState(lead.additionalInfo || '');
  const [localFavorite, setLocalFavorite] = useState(lead.isFavorite || false);
  const [localRevenue, setLocalRevenue] = useState(lead.potentialRevenue || '');
  const [localTag, setLocalTag] = useState(lead.tag || '');
  const [localSector, setLocalSector] = useState(lead.sectorActivity || '');
  const [localCustomFields, setLocalCustomFields] = useState<Record<string, any>>(lead.customFields || {});

  // États "Autre montant"
  const isCustomRevenue = localRevenue !== '' && !effectiveBrackets.includes(localRevenue);
  const [showCustomInput, setShowCustomInput] = useState(isCustomRevenue);
  const [customInputValue, setCustomInputValue] = useState(isCustomRevenue ? localRevenue : '');
  const [localStatus, setLocalStatus] = useState(lead.status);
  const [localAssignedTo, setLocalAssignedTo] = useState(lead.assignedTo); // NOUVEAU

  // Mode édition global : un seul crayon en haut → bascule toute la sidebar en
  // édition. L'utilisateur peut modifier plusieurs champs avant de sauvegarder
  // en un seul appel API. Voir handleSaveAll / handleCancelAll.

  // États édition contact
  // En mode création, on démarre déjà en édition pour que tous les champs
  // soient saisissables d'emblée (la fiche s'ouvre vierge, l'utilisateur remplit).
  const [editingContact, setEditingContact] = useState(isCreating);
  const [editName, setEditName] = useState(lead.name);
  const [editCompany, setEditCompany] = useState(lead.clientCompanyName || '');
  const [editEmail, setEditEmail] = useState(lead.email);
  const [editPhone, setEditPhone] = useState(lead.phone || '');
  const [editPhoneLandline, setEditPhoneLandline] = useState(lead.phoneLandline || '');

  // (Toast local retiré — utilise désormais le store global services/toast.ts)

  // État pour confirmation suppression RDV (timeline)
  const [deleteConfirmApptId, setDeleteConfirmApptId] = useState<string | null>(null);

  // --- GESTION RDV (UNIFIÉE AVEC CALENDARVIEW) ---
  const [showApptModal, setShowApptModal] = useState(false);
  const [editingApptId, setEditingApptId] = useState<string | null>(null);
  const [isSavingAppt, setIsSavingAppt] = useState(false);
  const [apptForm, setApptForm] = useState({
      title: '',
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '10:00',
      type: AppointmentType.PHONE,
      description: ''
  });

  // Vérifier si l'utilisateur peut réassigner (Admin, Chef d'équipe ou Super Admin)
  const canReassign = currentUser.role === UserRole.COMPANY_ADMIN
    || currentUser.role === UserRole.TEAM_MANAGER
    || currentUser.role === UserRole.SUPER_ADMIN;

  // --- INITIALISATION ---
  useEffect(() => {
    setLocalAdditionalInfo(prev => lead.additionalInfo || prev);
    if (lead.potentialRevenue) setLocalRevenue(lead.potentialRevenue);
    if (lead.isFavorite !== undefined) setLocalFavorite(lead.isFavorite);
    setLocalTag(lead.tag || '');
    setLocalSector(lead.sectorActivity || '');
    setLocalStatus(lead.status);
    setLocalAssignedTo(lead.assignedTo);
    setEditName(lead.name);
    setEditCompany(lead.clientCompanyName || '');
    setEditEmail(lead.email);
    setEditPhone(lead.phone || '');
    setEditPhoneLandline(lead.phoneLandline || '');
    // En création, on garde l'édition active pour que tous les champs restent saisissables
    setEditingContact(isCreating);

    // Reset tabs & forms - Ouvrir sur commentaires par défaut
    setActiveTab('comments');
    setEmailSubject(`Suivi dossier / ${lead.name}`);
    setOppSubject('');
    setOppComment('');
    setOppItems([{ label: '', quantity: 1, unitPrice: 0 }]);
    setTaskTitle('');
    setTaskComment('');
    setTaskDueAt(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
  }, [lead.id]);

  useEffect(() => {
    // En création, aucune donnée à charger (pas de leadId persistant) — on charge
    // uniquement les secteurs pour alimenter le select.
    if (isCreating) {
      api.getActivitySectors().then(s => setAvailableSectors(s || [])).catch(() => {});
      return;
    }
    const fetchData = async () => {
      const [cmtData, apptData, actData, oppData, taskData] = await Promise.all([
        api.getComments(lead.id),
        api.getAppointments({ leadId: lead.id }),
        api.getLeadActivities(lead.id),
        api.getOpportunities({
          companyId: lead.companyId,
          clientCompanyName: lead.clientCompanyName || undefined,
          leadId: lead.clientCompanyName ? undefined : lead.id
        }),
        api.getTasks({ companyId: lead.companyId })
      ]);
      setComments(cmtData || []);
      setAppointments(apptData || []);
      setActivities(actData || []);
      setOpportunities(
        (oppData || []).filter(o =>
          lead.clientCompanyName
            ? o.clientCompanyName === lead.clientCompanyName
            : String(o.leadId || '') === String(lead.id)
        )
      );
      setTasks(
        (taskData || []).filter(t =>
          String(t.leadId || '') === String(lead.id) ||
          (lead.clientCompanyName && t.clientCompanyName === lead.clientCompanyName)
        )
      );
      const sectorData = await api.getActivitySectors();
      setAvailableSectors(sectorData || []);
      // Load call logs & attachments
      try {
        const [logs, atts] = await Promise.all([
          api.getCallLogs({ leadId: lead.id, limit: 20 }),
          api.getAttachments(lead.id)
        ]);
        setCallLogs(logs);
        setCallLogsLoaded(true);
        setAttachments(atts);
      } catch { /* ignore */ }
      // Load email templates
      try {
        const tpls = await api.getEmailTemplates();
        setEmailTemplates(tpls);
      } catch { /* ignore */ }
    };
    fetchData();
  }, [lead.id]);

  // --- UTILS ---
  // Compat shim : on garde le nom showToast pour minimiser le diff, mais
  // l'implémentation passe par le store global. Le type 'info' historique
  // était utilisé pour des erreurs ET des infos ; on mappe selon le préfixe.
  const showToast = (msg: string, type: 'success' | 'info') => {
      if (type === 'success') { toast.success(msg); return; }
      const lc = msg.toLowerCase();
      if (lc.startsWith('erreur') || lc.startsWith('echec') || lc.startsWith('échec')) {
          toast.error(msg);
      } else {
          toast.info(msg);
      }
  };

  // --- ACTIONS LEAD ---
  const toggleFavorite = async () => {
    const newVal = !localFavorite;
    setLocalFavorite(newVal); 
    try {
        const updated = await api.updateLead(lead.id, { isFavorite: newVal }, currentUser);
        onUpdate(updated); 
        if(newVal) showToast("Ajouté aux favoris", 'success');
        
        // Recharger les activités
        const actData = await api.getLeadActivities(lead.id);
        setActivities(actData || []);
    } catch (e) {
        setLocalFavorite(!newVal);
    }
  };

  // Sauvegarde globale : un seul appel API qui regroupe TOUTES les
  // modifications faites pendant la session d'édition (contact, tag, secteur,
  // assigné à, revenu potentiel, notes internes, champs personnalisés).
  const handleSaveAll = async () => {
    // En création, on construit la fiche complète depuis les états locaux
    // et on délègue à `onCreate` (le parent appellera api.createLead).
    if (isCreating) {
      const nameTrim = (editName || '').trim();
      const companyTrim = (editCompany || '').trim();
      if (!nameTrim && !companyTrim) {
        showToast("Renseignez au moins le nom complet ou le nom de l'entreprise.", 'info');
        return;
      }
      const emailTrim = (editEmail || '').trim();
      if (emailTrim) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailTrim)) {
          showToast("Email invalide.", 'info');
          return;
        }
      }
      try {
        await onCreate?.({
          companyId: currentUser.companyId,
          assignedTo: localAssignedTo || currentUser.id,
          clientCompanyName: companyTrim,
          tag: (localTag || '').trim(),
          sectorActivity: localSector || '',
          name: nameTrim || companyTrim,
          email: emailTrim,
          phone: (editPhone || '').trim(),
          phoneLandline: (editPhoneLandline || '').trim(),
          status: localStatus || 'new',
          additionalInfo: localAdditionalInfo || '',
          potentialRevenue: localRevenue || '',
          isFavorite: localFavorite,
          source: '',
          customFields: localCustomFields,
        });
        // onClose est déclenché par le parent après création réussie.
      } catch (e: any) {
        showToast(e?.message?.startsWith('DUPLICATE:') ? 'Un prospect similaire existe déjà' : 'Erreur lors de la création', 'info');
      }
      return;
    }

    const changes: Partial<Lead> = {};
    if (editName.trim() && editName !== lead.name) changes.name = editName.trim();
    if (editCompany !== (lead.clientCompanyName || '')) changes.clientCompanyName = editCompany.trim();
    if (editEmail !== lead.email) changes.email = editEmail.trim();
    if (editPhone !== (lead.phone || '')) changes.phone = editPhone.trim();
    if (editPhoneLandline !== (lead.phoneLandline || '')) changes.phoneLandline = editPhoneLandline.trim();
    if ((localTag || '') !== (lead.tag || '')) changes.tag = localTag.trim();
    if ((localSector || '') !== (lead.sectorActivity || '')) changes.sectorActivity = localSector;
    if (String(localAssignedTo || '') !== String(lead.assignedTo || '')) changes.assignedTo = localAssignedTo;
    if ((localAdditionalInfo || '') !== (lead.additionalInfo || '')) changes.additionalInfo = localAdditionalInfo;
    if ((localRevenue || '') !== (lead.potentialRevenue || '')) changes.potentialRevenue = localRevenue;

    // Custom fields : compare clé par clé pour éviter d'envoyer un payload
    // identique si l'utilisateur n'a rien touché.
    const initialCustom = lead.customFields || {};
    const customDirty =
      Object.keys(localCustomFields).length !== Object.keys(initialCustom).length ||
      Object.entries(localCustomFields).some(([k, v]) => initialCustom[k] !== v);
    if (customDirty) (changes as any).customFields = localCustomFields;

    if (Object.keys(changes).length === 0) { setEditingContact(false); return; }
    try {
      const updated = await api.updateLead(lead.id, changes, currentUser);
      onUpdate(updated);
      setEditingContact(false);
      showToast('Modifications enregistrées', 'success');
      const actData = await api.getLeadActivities(lead.id);
      setActivities(actData || []);
    } catch (e) {
      showToast('Erreur lors de la mise à jour', 'info');
    }
  };

  // Annule toutes les modifications locales et sort du mode édition.
  const handleCancelAll = () => {
    // En création : rien à annuler (lead vierge), on ferme la modale.
    if (isCreating) { onClose(); return; }
    setEditName(lead.name);
    setEditCompany(lead.clientCompanyName || '');
    setEditEmail(lead.email);
    setEditPhone(lead.phone || '');
    setEditPhoneLandline(lead.phoneLandline || '');
    setLocalTag(lead.tag || '');
    setLocalSector(lead.sectorActivity || '');
    setLocalAssignedTo(lead.assignedTo);
    setLocalAdditionalInfo(lead.additionalInfo || '');
    setLocalRevenue(lead.potentialRevenue || '');
    setLocalCustomFields(lead.customFields || {});
    setShowCustomInput(false);
    setCustomInputValue('');
    setEditingContact(false);
  };

  const handleUpdateLeadInfo = async () => {
    if (localAdditionalInfo !== lead.additionalInfo) {
      const updated = await api.updateLead(lead.id, { additionalInfo: localAdditionalInfo }, currentUser);
      onUpdate(updated);
      showToast('Notes mises à jour', 'success');
      
      // Recharger les activités
      const actData = await api.getLeadActivities(lead.id);
      setActivities(actData || []);
    }
  };

  const handleUpdateTag = async () => {
    if ((localTag || '') === (lead.tag || '')) return;
    const updated = await api.updateLead(lead.id, { tag: localTag.trim() }, currentUser);
    onUpdate(updated);
    showToast('Tag mis a jour', 'success');
  };

  const handleUpdateSector = async (nextSector: string) => {
    setLocalSector(nextSector);
    const updated = await api.updateLead(lead.id, { sectorActivity: nextSector }, currentUser);
    onUpdate(updated);
    showToast('Secteur mis a jour', 'success');
  };

  const updateRevenue = async (bracket: string) => {
    const newValue = localRevenue === bracket ? '' : bracket;
    setLocalRevenue(newValue);
    setShowCustomInput(false);
    const updated = await api.updateLead(lead.id, { potentialRevenue: newValue }, currentUser);
    onUpdate(updated);
    const actData = await api.getLeadActivities(lead.id);
    setActivities(actData || []);
  };

  const handleCustomChipClick = () => {
    if (showCustomInput) {
      setShowCustomInput(false);
    } else {
      setShowCustomInput(true);
      // Déselectionner le palier actif s'il y en a un
      if (effectiveBrackets.includes(localRevenue)) {
        setLocalRevenue('');
      }
      setCustomInputValue(isCustomRevenue ? localRevenue : '');
    }
  };

  const saveCustomRevenue = async () => {
    const val = customInputValue.trim();
    if (!val) return;
    setLocalRevenue(val);
    const updated = await api.updateLead(lead.id, { potentialRevenue: val }, currentUser);
    onUpdate(updated);
    const actData = await api.getLeadActivities(lead.id);
    setActivities(actData || []);
  };

  const handleStatusChange = async (newStatus: Lead['status']) => {
    setLocalStatus(newStatus);
    // En création, le lead n'existe pas encore : on se contente d'ajuster
    // l'état local. Le statut sera persisté dans onCreate au moment du save.
    if (isCreating) return;
    const updated = await api.updateLead(lead.id, { status: newStatus }, currentUser);
    onUpdate(updated);
    showToast(`Statut changé : ${newStatus.toUpperCase()}`, 'info');

    // Recharger les activités
    const actData = await api.getLeadActivities(lead.id);
    setActivities(actData || []);
  };

  // Push lead to ERP
  const handlePushToErp = async () => {
    if (erpPushState === 'pushing' || erpPushState === 'done' || erpPushState === 'already') return;
    setErpPushState('pushing');
    try {
      const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';
      const token = sessionStorage.getItem('orion_access_token') || '';
      const res = await fetch(`${API_BASE}?action=push_to_erp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ lead_id: lead.id }),
      });
      const data = await res.json();
      if (data.ok) {
        setErpCustomerId(data.erp_customer_id);
        setErpPushState(data.already_pushed ? 'already' : 'done');
        showToast(data.already_pushed ? 'Déjà envoyé vers l\'ERP' : 'Lead envoyé vers l\'ERP ✅', data.already_pushed ? 'info' : 'success');
      } else {
        setErpPushState('error');
        showToast(`Erreur ERP: ${data.error || 'Echec'}`, 'info');
      }
    } catch (err) {
      setErpPushState('error');
      showToast('Erreur de connexion avec l\'ERP', 'info');
    }
  };

  // NOUVELLE FONCTION : Réassigner le prospect
  const handleAssignedToChange = async (newAssignedTo: string) => {
    if (newAssignedTo === localAssignedTo) return; // Pas de changement
    
    setLocalAssignedTo(newAssignedTo);
    const updated = await api.updateLead(lead.id, { assignedTo: newAssignedTo }, currentUser);
    onUpdate(updated);
    
    const assignedUser = companyUsers.find(u => u.id === newAssignedTo);
    showToast(`Prospect assigné à ${assignedUser?.name || 'Inconnu'}`, 'success');
    
    // Recharger les activités
    const actData = await api.getLeadActivities(lead.id);
    setActivities(actData || []);
  };

  // --- ACTIONS TIMELINE / EMAIL ---
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const added = await api.addComment({
        leadId: lead.id,
        userId: currentUser.id,
        userName: currentUser.name,
        content: newComment
      }, currentUser);
      setComments([added, ...comments]);
      setNewComment('');
      showToast("Note ajoutée", 'success');
      
      // Recharger les activités
      const actData = await api.getLeadActivities(lead.id);
      setActivities(actData || []);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!emailBody.trim() || !emailSubject.trim()) return;
      if(!lead.email) { showToast('Ce prospect n\'a pas d\'email', 'info'); return; }

      // Avertir si certaines variables {{...}} ne pourront pas être résolues à l'envoi.
      // (le serveur renderise depuis la DB, on simule ici avec le contexte courant pour repérer les fautes de frappe).
      const ctx = buildEmailTemplateContext({ lead, user: currentUser, company });
      const previewSubject = renderEmailTemplate(emailSubject, ctx);
      const previewBody    = renderEmailTemplate(emailBody, ctx);
      const unresolved = Array.from(new Set([
        ...findUnresolvedEmailPlaceholders(previewSubject),
        ...findUnresolvedEmailPlaceholders(previewBody),
      ]));
      if (unresolved.length > 0) {
        const ok = window.confirm(
          `Variable${unresolved.length > 1 ? 's' : ''} non résolue${unresolved.length > 1 ? 's' : ''} : ` +
          unresolved.map(v => `{{${v}}}`).join(', ') +
          `\n\nL'email sera envoyé tel quel. Continuer ?`
        );
        if (!ok) return;
      }

      setIsSubmitting(true);
      try {
        const API_BASE = 'api/index.php';
        const token = sessionStorage.getItem('orion_access_token') || '';

        // 1) Upload les fichiers PC dans les fichiers du lead (même mécanisme que l'onglet FICHIERS)
        const allFileIds = [...emailLeadFiles];
        console.log('[EMAIL] Début envoi. PJ PC:', emailAttachments.length, 'PJ Lead:', emailLeadFiles.length);
        for (const file of emailAttachments) {
          try {
            console.log('[EMAIL] Upload fichier:', file.name, file.size, 'bytes');
            const uploaded = await api.uploadAttachment(lead.id, file);
            console.log('[EMAIL] Upload résultat:', uploaded);
            if (uploaded?.id) {
              allFileIds.push(String(uploaded.id));
            } else {
              console.error('[EMAIL] Upload OK mais pas d\'ID retourné:', uploaded);
            }
          } catch (err) {
            console.error('[EMAIL] Erreur upload PJ:', err);
            showToast(`Erreur upload: ${file.name}`, 'info');
          }
        }

        // Rafraîchir la liste des fichiers du lead
        if (emailAttachments.length > 0) {
          const freshAttachments = await api.getAttachments(lead.id);
          setAttachments(freshAttachments || []);
        }

        console.log('[EMAIL] Envoi mail avec fichiers IDs:', allFileIds);

        // 2) Envoyer le mail en JSON avec les IDs des fichiers à joindre
        const res = await fetch(`${API_BASE}?action=send_email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            to: lead.email, subject: emailSubject, body: emailBody,
            fromName: currentUser.name, lead_id: lead.id,
            lead_file_ids: allFileIds
          })
        });
        const result = await res.json();

        // Logger dans les commentaires + timeline
        const pjLabel = allFileIds.length > 0 ? `\n📎 ${allFileIds.length} pièce(s) jointe(s)` : '';
        const added = await api.addComment({
            leadId: lead.id,
            userId: currentUser.id,
            userName: currentUser.name,
            content: `📧 EMAIL ${result.success ? 'ENVOYÉ' : 'ECHEC'}\nDestinataire: ${lead.email}\nObjet: ${emailSubject}${pjLabel}\n\n${emailBody}`
        }, currentUser);
        setComments([added, ...comments]);

        setEmailBody('');
        setEmailSubject('');
        setEmailAttachments([]);
        setEmailLeadFiles([]);
        const pjInfo = result.attachments_count > 0 ? ` (${result.attachments_count} pièce(s) jointe(s))` : '';
        showToast(result.success ? `Email envoyé à ${lead.email}${pjInfo}` : 'Echec envoi (log archivé)', result.success ? 'success' : 'info');
        setActiveTab('comments');

        const actData = await api.getLeadActivities(lead.id);
        setActivities(actData || []);
      } catch (err) {
        console.error('Erreur envoi email:', err);
        showToast('Erreur technique envoi email', 'info');
      } finally {
        setIsSubmitting(false);
      }
  };

  // --- EMAIL TEMPLATES ---
  const handleApplyTemplate = (tpl: { subject: string; body: string }) => {
    const ctx = buildEmailTemplateContext({ lead, user: currentUser, company });
    setEmailSubject(renderEmailTemplate(tpl.subject, ctx));
    setEmailBody(renderEmailTemplate(tpl.body, ctx));
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const result = await api.saveEmailTemplate({ name: templateName, subject: emailSubject, body: emailBody });
      setEmailTemplates(prev => [...prev, { id: result.id, name: templateName, subject: emailSubject, body: emailBody, created_at: new Date().toISOString() }]);
      setTemplateName('');
      setShowSaveTemplate(false);
      showToast('Template sauvegardé', 'success');
    } catch { showToast('Erreur sauvegarde template', 'info'); }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await api.deleteEmailTemplate(id);
      setEmailTemplates(prev => prev.filter(t => t.id !== id));
    } catch { showToast('Erreur suppression template', 'info'); }
  };

  const addOppItemLine = () => {
    setOppItems(prev => [...prev, { label: '', quantity: 1, unitPrice: 0 }]);
  };

  const updateOppItemLine = (index: number, patch: Partial<{ label: string; quantity: number; unitPrice: number }>) => {
    setOppItems(prev => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const removeOppItemLine = (index: number) => {
    setOppItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oppSubject.trim()) {
      toast.warning("L'objet de la proposition est obligatoire.");
      return;
    }
    if (oppItems.some(line => !line.label.trim() || line.quantity <= 0 || line.unitPrice < 0)) {
      toast.warning('Corrigez les lignes produits / services.');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await api.createOpportunity(
        {
          companyId: lead.companyId,
          leadId: lead.id,
          clientCompanyName: lead.clientCompanyName || 'Client',
          subject: oppSubject.trim(),
          comment: oppComment.trim(),
          createdBy: currentUser.id,
          items: oppItems.map(i => ({
            label: i.label.trim(),
            quantity: Number(i.quantity),
            unitPrice: Number(i.unitPrice)
          }))
        },
        currentUser
      );
      setOpportunities(prev => [created, ...prev]);
      setOppSubject('');
      setOppComment('');
      setOppItems([{ label: '', quantity: 1, unitPrice: 0 }]);
      showToast('Opportunité créée', 'success');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskTitle.trim() || !taskDueAt) {
      toast.warning('Titre et échéance requis.');
      return;
    }
    setIsSubmitting(true);
    try {
      const created = await api.createTask({
        companyId: lead.companyId,
        leadId: lead.id,
        clientCompanyName: lead.clientCompanyName || undefined,
        title: taskTitle.trim(),
        comment: taskComment.trim(),
        dueAt: new Date(taskDueAt).toISOString()
      }, currentUser);
      setTasks(prev => [created, ...prev]);
      setTaskTitle('');
      setTaskComment('');
      setTaskDueAt(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
      await api.checkUpcomingTaskReminders(currentUser.id);
      showToast('Tâche créée', 'success');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTaskDone = async (task: Task) => {
    const updated = await api.updateTask(task.id, { status: task.status === 'done' ? 'todo' : 'done' });
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
  };

  const editTask = async (task: Task) => {
    const nextTitle = window.prompt('Titre de la tâche', task.title);
    if (nextTitle === null) return;
    const nextComment = window.prompt('Commentaire', task.comment || '');
    if (nextComment === null) return;
    const defaultDate = new Date(task.dueAt).toISOString().slice(0, 16);
    const nextDue = window.prompt('Échéance (YYYY-MM-DDTHH:mm)', defaultDate);
    if (nextDue === null) return;

    const updated = await api.updateTask(task.id, {
      title: nextTitle.trim(),
      comment: nextComment.trim(),
      dueAt: new Date(nextDue).toISOString()
    });
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
  };

  const [confirmDeleteTaskInModal, setConfirmDeleteTaskInModal] = useState<string | null>(null);
  const handleDeleteTaskInModal = async (taskId: string) => {
    try {
      await api.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setConfirmDeleteTaskInModal(null);
    } catch (e) { console.error('Erreur suppression tache:', e); }
  };

  // --- GESTION RDV (LOGIQUE) ---
  const openNewApptModal = () => {
      setEditingApptId(null);
      setApptForm({
          title: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '10:00',
          type: AppointmentType.PHONE,
          description: ''
      });
      setShowApptModal(true);
  };

  const openEditApptModal = (appt: Appointment) => {
      setEditingApptId(appt.id);
      const start = new Date(appt.start);
      const end = new Date(appt.end);
      
      setApptForm({
          title: appt.title,
          date: start.toISOString().split('T')[0],
          startTime: start.toTimeString().slice(0, 5),
          endTime: end.toTimeString().slice(0, 5),
          type: appt.type,
          description: appt.description || ''
      });
      setShowApptModal(true);
  };

  const handleSaveAppt = async (e: React.FormEvent) => {
      e.preventDefault();
      if (isSavingAppt) return;
      if (!apptForm.title.trim()) return;

      const start = new Date(`${apptForm.date}T${apptForm.startTime}`).toISOString();
      const end = new Date(`${apptForm.date}T${apptForm.endTime}`).toISOString();

      setIsSavingAppt(true);
      try {
          if(editingApptId) {
              await api.updateAppointment(editingApptId, {
                  title: apptForm.title,
                  start,
                  end,
                  type: apptForm.type,
                  description: apptForm.description
              });

              const updatedAppts = appointments.map(a => a.id === editingApptId ? { ...a, title: apptForm.title, start, end, type: apptForm.type, description: apptForm.description } : a);
              setAppointments(updatedAppts);
              showToast('RDV modifié', 'info');
          } else {
              const newAppt = await api.addAppointment({
                  leadId: lead.id,
                  userId: currentUser.id,
                  title: apptForm.title,
                  start,
                  end,
                  type: apptForm.type,
                  description: apptForm.description,
                  clientEmail: lead.email
              }, currentUser);

              setAppointments([newAppt, ...appointments]);
              showToast('RDV créé', 'success');

              // Recharger les activités
              const actData = await api.getLeadActivities(lead.id);
              setActivities(actData || []);
          }
          setShowApptModal(false);
      } finally {
          setIsSavingAppt(false);
      }
  };

  const handleDeleteAppt = async (id: string) => {
      await api.deleteAppointment(id);
      setAppointments(appointments.filter(a => a.id !== id));
      setDeleteConfirmApptId(null);
      showToast('RDV supprimé', 'info');
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 5) return "à l'instant";
    if (mins < 60) return `il y a ${mins} min`;
    if (hrs < 24) return `il y a ${hrs}h`;
    if (days < 7) return `il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const normalizePhoneForDial = (raw?: string) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('sip:')) return trimmed;

    let cleaned = trimmed.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('00')) cleaned = `+${cleaned.slice(2)}`;
    return cleaned;
  };

  // Compose un numéro précis (mobile ou fixe). On essaie successivement plusieurs
  // schémas d'URI pour couvrir les softphones les plus courants (MicroSIP,
  // Zoiper, Linphone, Cisco Jabber via callto:, fallback OS via tel:).
  // L'utilisateur peut prioriser son softphone via VITE_DIALER_PROTOCOL :
  //   sip (défaut), microsip, zoiper, linphone, callto, tel.
  const handleCallClient = (rawNumber?: string) => {
    const dialTarget = normalizePhoneForDial(rawNumber ?? lead.phone);
    if (!dialTarget) {
      toast.warning('Numéro de téléphone manquant ou invalide.');
      return;
    }

    const targetWithoutScheme = dialTarget.replace(/^sip:/, '');
    // Linphone attend une URI SIP complète après son schéma, les autres
    // acceptent juste le numéro.
    const linphoneUri = `linphone:sip:${targetWithoutScheme}`;
    const allSchemes: Record<string, string> = {
      sip:      `sip:${targetWithoutScheme}`,
      microsip: `microsip:${targetWithoutScheme}`,
      zoiper:   `zoiper:${targetWithoutScheme}`,
      linphone: linphoneUri,
      callto:   `callto:${targetWithoutScheme}`,
      tel:      `tel:${targetWithoutScheme}`,
    };
    const defaultOrder = ['sip', 'microsip', 'zoiper', 'linphone', 'callto', 'tel'];
    const preferredProtocol = (import.meta.env.VITE_DIALER_PROTOCOL || 'sip').toLowerCase();
    const order = defaultOrder.includes(preferredProtocol)
      ? [preferredProtocol, ...defaultOrder.filter(p => p !== preferredProtocol)]
      : defaultOrder;
    const candidates = order.map(p => allSchemes[p]);

    // Essaye plusieurs protocoles pour couvrir les differents enregistrements Windows/MicroSIP.
    candidates.forEach((uri, index) => {
      setTimeout(() => {
        window.location.href = uri;
      }, index * 350);
    });

  };

  const handleLogCall = async () => {
    const durationSec = parseInt(callDuration) || 0;
    if (durationSec <= 0) return;
    try {
      await api.logCall({
        leadId: lead.id,
        duration: durationSec,
        direction: callDirection,
        notes: callNotes
      });

      // Logger dans les commentaires (même UX que pour les emails) :
      // un bloc apparaît dans l'onglet Commentaires avec direction, durée
      // formatée et notes saisies. Permet de retrouver l'appel dans le fil.
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      const durationLabel = minutes > 0
        ? `${minutes}m${String(seconds).padStart(2, '0')}s`
        : `${seconds}s`;
      const directionLabel = callDirection === 'inbound' ? 'ENTRANT' : 'SORTANT';
      const notesPart = callNotes.trim() ? `\nNotes: ${callNotes.trim()}` : '';
      try {
        const added = await api.addComment({
          leadId: lead.id,
          userId: currentUser.id,
          userName: currentUser.name,
          content: `📞 APPEL ${directionLabel}\nDurée: ${durationLabel}${notesPart}`,
        }, currentUser);
        setComments(prev => [added, ...prev]);
      } catch (e) {
        // Silencieux : l'appel est enregistré dans `call_logs`, le commentaire
        // n'est qu'un confort visuel — on ne fait pas échouer l'action globale.
        console.warn('Commentaire log d\'appel non créé', e);
      }

      setShowCallLog(false);
      setCallDuration('');
      setCallNotes('');
      toast.success('Appel enregistré');
      // Reload activities & call logs
      const [acts, logs] = await Promise.all([
        api.getLeadActivities(lead.id),
        api.getCallLogs({ leadId: lead.id, limit: 20 })
      ]);
      setActivities(acts);
      setCallLogs(logs);
      setActiveTab('comments');
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement de l'appel");
    }
  };

  const loadCallLogs = async () => {
    if (callLogsLoaded) return;
    try {
      const logs = await api.getCallLogs({ leadId: lead.id, limit: 20 });
      setCallLogs(logs);
      setCallLogsLoaded(true);
    } catch { /* ignore */ }
  };

  const effectiveStatuses = (customStatuses && customStatuses.length > 0) ? customStatuses : DEFAULT_STATUSES;
  const statusOptions = effectiveStatuses.map(s => ({ value: s.id, label: s.label, color: s.color }));

  const currentStatus = statusOptions.find(s => s.value === localStatus);

  const modalContent = (
    <div className="orion-modal-backdrop fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={onClose}></div>
      <div className="orion-modal-panel orion-lead-modal relative w-full max-w-7xl h-[90vh] flex flex-col rounded-[3rem] shadow-2xl border animate-in zoom-in-95 overflow-hidden">
        
        {/* (Toast inline retiré — affichage global via ToastContainer monté dans App.tsx) */}

        {/* HEADER */}
        <div className="orion-lead-modal-header flex-none px-4 md:px-8 py-4 md:py-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <h2 className="text-lg md:text-2xl font-black text-slate-900 dark:text-white tracking-tight truncate">{isCreating ? 'Nouveau prospect' : lead.name}</h2>
              {!isCreating && showAction('favorite') && (<button type="button" onClick={toggleFavorite} title={localFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'} aria-label={localFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                <svg className={`w-5 h-5 ${localFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill={localFavorite ? 'currentColor' : 'none'}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </button>)}
            </div>
            <div className="flex items-center gap-2">
              <SelectField
                value={localStatus}
                onChange={(val) => handleStatusChange(val as Lead['status'])}
                options={statusOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                className="!text-white text-xs font-bold !rounded-full shadow-lg !py-1.5 !px-4 !border-0"
                style={{ backgroundColor: currentStatus?.color }}
              />
              {/* Le bouton « Appeler » du header a été remplacé par des
                  pictogrammes inline en bout de ligne mobile/fixe dans la
                  sidebar contact (composition directe par numéro). */}
              {!isCreating && localStatus === 'won' && (
                <button type="button"
                  onClick={handlePushToErp}
                  disabled={erpPushState === 'pushing' || erpPushState === 'done' || erpPushState === 'already'}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wide transition-all shadow-lg ${
                    erpPushState === 'done' || erpPushState === 'already'
                      ? 'bg-emerald-100 text-emerald-700 cursor-default'
                      : erpPushState === 'pushing'
                      ? 'bg-amber-100 text-amber-700 cursor-wait'
                      : erpPushState === 'error'
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                  }`}
                  title={erpCustomerId ? `ERP Client #${erpCustomerId}` : 'Envoyer ce lead vers l\'ERP'}
                >
                  {erpPushState === 'pushing' ? '⏳' : erpPushState === 'done' || erpPushState === 'already' ? '✅' : erpPushState === 'error' ? '⚠️' : '📤'}
                  {erpPushState === 'pushing' ? 'Envoi...' : erpPushState === 'done' ? 'Envoyé' : erpPushState === 'already' ? 'Déjà lié' : erpPushState === 'error' ? 'Réessayer' : 'ERP'}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isCreating && onDelete && (currentUser.role === UserRole.COMPANY_ADMIN || currentUser.role === UserRole.TEAM_MANAGER || currentUser.role === UserRole.SUPER_ADMIN) && (
              confirmDeleteLead ? (
                <div className="flex items-center gap-1.5 animate-in fade-in">
                  <span className="text-xs font-bold text-rose-400">Supprimer ?</span>
                  <button type="button" onClick={() => onDelete!(lead.id)} className="px-3 py-1.5 rounded-lg text-[10px] font-black bg-rose-500 text-white hover:bg-rose-600 transition-colors">Oui</button>
                  <button type="button" onClick={() => setConfirmDeleteLead(false)} className="px-3 py-1.5 rounded-lg text-[10px] font-black bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 transition-colors">Non</button>
                </div>
              ) : (
                <button type="button" onClick={() => setConfirmDeleteLead(true)} className="p-2 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-xl transition-colors text-slate-400 hover:text-rose-500" title="Supprimer le prospect">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              )
            )}
            <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* CONTENT — attend le chargement du template pour éviter le flash de réorganisation */}
        {!sheetTemplateReady ? (
          <div className="orion-lead-modal-body flex-1 flex items-center justify-center">
            <div className="flex items-center gap-3 text-slate-400">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              <span className="text-sm font-medium">Chargement…</span>
            </div>
          </div>
        ) : (
        <div className="orion-lead-modal-body flex-1 flex overflow-hidden">
          {/* LEFT SIDEBAR — hidden on mobile (accessible via onglet Infos) */}
          <div className={`orion-lead-modal-side border-r border-slate-100 dark:border-slate-800 p-6 space-y-1.5 overflow-y-auto
            w-full md:w-80
            ${activeTab === 'info' ? 'flex md:flex flex-col' : 'hidden md:flex md:flex-col'}
          `}>
            {/* En-tête contact : libellé + toggle édition. Les champs (entreprise,
                email, téléphones, bouton Appeler) sont rendus comme des nodes
                positionnables plus bas selon la config sheetCfg.fieldOrder. */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Contact</label>
                {!editingContact ? (
                  <button type="button" onClick={() => setEditingContact(true)} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-indigo-400 transition-colors" title="Modifier la fiche">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={handleSaveAll} className="p-1 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 text-green-500 transition-colors" title="Enregistrer toutes les modifications">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </button>
                    <button type="button" onClick={handleCancelAll} className="p-1 rounded-md hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-400 transition-colors" title="Annuler les modifications">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                )}
              </div>
              {editingContact && (
                <div className="flex items-center gap-2 text-sm mt-2">
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nom" className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-800 dark:text-white outline-none focus:border-indigo-400" />
                </div>
              )}
            </div>

            {/* Bloc « action appel » fixe : log manuel + historique des appels.
                Le bouton « Appeler » lui-même est désormais dans l'en-tête. */}
            {/* Le bouton « Enregistrer un appel » a été déplacé en pictogramme
                inline à droite de chaque ligne téléphone. On garde uniquement
                l'historique des derniers appels ici. */}
            {callLogs.length > 0 && (
              <div className="space-y-1.5">
                {callLogs.slice(0, 3).map((cl: any, i: number) => (
                  <div key={i} className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <svg className={`w-3 h-3 flex-shrink-0 ${cl.direction === 'inbound' ? 'text-green-400' : 'text-cyan-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-1.88.94a11.042 11.042 0 005.329 5.329l.94-1.88a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="font-bold">{Math.floor(cl.duration / 60)}m{String(cl.duration % 60).padStart(2, '0')}s</span>
                    <span className="text-slate-600">·</span>
                    <span>{new Date(cl.call_date).toLocaleDateString('fr-FR')}</span>
                  </div>
                ))}
              </div>
            )}

            {(() => {
              // Construction des blocs de champs du panneau latéral, puis affichage dans
              // l'ordre configuré par le template (sheetCfg.fieldOrder).
              const nodes: Record<string, React.ReactNode> = {};

              // Helper : en-tête d'un champ éditable. Affiche le label à gauche
              // et un bouton crayon (lecture) ou ✓/✕ (édition) à droite.
              // Header simple : juste le label. L'édition est désormais pilotée
              // par le crayon global du Contact (editingContact). Les paramètres
              // optionnels onSave/onCancel sont conservés pour signature mais
              // ignorés.
              const editHeader = (_key: string, label: string, _onSave?: () => void | Promise<void>, _onCancel?: () => void) => (
                <div className="mb-0.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">{label}</label>
                </div>
              );

              const readOnlyText = (val: string | undefined | null) => (
                <p className={`text-sm ${val ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                  {val || 'Non renseigné'}
                </p>
              );

              // ===== Bloc CONTACT — chaque champ devient un node positionnable =====
              if (showField('clientCompanyName')) {
                nodes.clientCompanyName = (
                  <div key="clientCompanyName" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M5 7v11a2 2 0 002 2h10a2 2 0 002-2V7M9 7V5a3 3 0 016 0v2" /></svg>
                    {editingContact ? (
                      <input value={editCompany} onChange={e => setEditCompany(e.target.value)} placeholder="Entreprise" aria-label="Entreprise" className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-800 dark:text-white outline-none focus:border-indigo-400" />
                    ) : (lead.clientCompanyName || 'Client non renseigne')}
                  </div>
                );
              }

              if (showField('email')) {
                nodes.email = (
                  <div key="email" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    {editingContact ? (
                      <input value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="Email" aria-label="Email" type="email" className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-800 dark:text-white outline-none focus:border-indigo-400" />
                    ) : displayEmail(lead.email, currentUser.role, maskContacts)}
                  </div>
                );
              }

              // Pictogramme « appeler ce numéro » : seulement en mode lecture
              // ET si la config tenant autorise l'action call ET si le numéro
              // est valide après normalisation.
              const callIconBtn = (rawNumber: string | undefined | null, label: string) => {
                if (editingContact || !showAction('call')) return null;
                if (!normalizePhoneForDial(rawNumber || '')) return null;
                return (
                  <button
                    type="button"
                    onClick={() => handleCallClient(rawNumber || '')}
                    title={label}
                    aria-label={label}
                    className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/25 text-emerald-400 hover:text-emerald-300 transition-colors flex-shrink-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-1.88.94a11.042 11.042 0 005.329 5.329l.94-1.88a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </button>
                );
              };

              // Pictogramme « Enregistrer un appel manuel » : ouvre la modale
              // de log d'appel pré-réglée en sortant. Affiché à côté du picto
              // de composition pour permettre un journal sans dial.
              const logCallIconBtn = () => {
                if (editingContact || !showAction('logCall')) return null;
                return (
                  <button
                    type="button"
                    onClick={() => { setShowCallLog(true); setCallDirection('outbound'); setCallDuration(''); setCallNotes(''); }}
                    title="Enregistrer un appel"
                    aria-label="Enregistrer un appel manuellement"
                    className="p-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/25 text-indigo-400 hover:text-indigo-300 transition-colors flex-shrink-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                );
              };

              // Wrapper inline pour aligner les pictos d'action à droite d'une ligne téléphone.
              const phoneActions = (rawNumber: string | undefined | null, callLabel: string) => {
                const dial = callIconBtn(rawNumber, callLabel);
                const log = logCallIconBtn();
                if (!dial && !log) return null;
                return (
                  <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                    {dial}
                    {log}
                  </div>
                );
              };

              if (showField('phone')) {
                nodes.phone = (
                  <div key="phone" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    {editingContact ? (
                      <input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="Mobile" aria-label="Téléphone mobile" type="tel" className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-800 dark:text-white outline-none focus:border-indigo-400" />
                    ) : (
                      <>
                        <span className="truncate">{displayPhone(lead.phone || '', currentUser.role, maskContacts) || 'Non renseigné'}</span>
                        {phoneActions(lead.phone, 'Appeler le mobile')}
                      </>
                    )}
                  </div>
                );
              }

              if (showField('phoneLandline')) {
                nodes.phoneLandline = (
                  <div key="phoneLandline" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" /></svg>
                    {editingContact ? (
                      <input value={editPhoneLandline} onChange={e => setEditPhoneLandline(e.target.value)} placeholder="Tél. fixe" aria-label="Téléphone fixe" type="tel" className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-sm text-slate-800 dark:text-white outline-none focus:border-indigo-400" />
                    ) : (
                      <>
                        <span className="truncate">{displayPhone(lead.phoneLandline || '', currentUser.role, maskContacts) || 'Non renseigné'}</span>
                        {phoneActions(lead.phoneLandline, 'Appeler le fixe')}
                      </>
                    )}
                  </div>
                );
              }

              // NB : le bouton « Appeler » est désormais dans l'en-tête de la
              // fiche prospect (à côté du statut). « Enregistrer un appel » et
              // l'historique restent dans la sidebar — voir le bloc fixe rendu
              // après l'en-tête « Contact ».

              if (showField('tag')) {
                nodes.tag = (
                  <div key="tag">
                    {editHeader('tag', 'Tag')}
                    {editingContact ? (
                      <input
                        type="text"
                        value={localTag}
                        onChange={(e) => setLocalTag(e.target.value)}
                        placeholder="ex: Prioritaire"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : readOnlyText(lead.tag)}
                  </div>
                );
              }

              if (showField('sectorActivity')) {
                const sectorLabel = lead.sectorActivity || '';
                nodes.sectorActivity = (
                  <div key="sectorActivity">
                    {editHeader('sectorActivity', "Secteur d'activité")}
                    {editingContact ? (
                      <SelectField
                        value={localSector}
                        onChange={(v) => setLocalSector(v)}
                        options={[
                          { value: '', label: 'Non renseigné' },
                          ...availableSectors.map(s => ({ value: s, label: s }))
                        ]}
                        className="w-full rounded-xl px-4 py-2 text-sm font-bold"
                      />
                    ) : readOnlyText(sectorLabel)}
                  </div>
                );
              }

              if (canReassign && showField('assignedTo')) {
                const assignedUser = companyUsers.find(u => String(u.id) === String(lead.assignedTo));
                const assignedLabel = assignedUser ? assignedUser.name + (assignedUser.id === currentUser.id ? ' (Vous)' : '') : '';
                nodes.assignedTo = (
                  <div key="assignedTo">
                    {editHeader('assignedTo', 'Assigné à')}
                    {editingContact ? (
                      <SelectField
                        value={localAssignedTo}
                        onChange={(v) => setLocalAssignedTo(v)}
                        options={[
                          { value: '', label: 'Non assigné' },
                          ...companyUsers.map(u => ({
                            value: u.id,
                            label: u.name + (u.id === currentUser.id ? ' (Vous)' : '')
                          }))
                        ]}
                        className="w-full rounded-xl px-4 py-2 text-sm font-bold"
                      />
                    ) : readOnlyText(assignedLabel)}
                  </div>
                );
              }

              if (showField('potentialRevenue')) {
                // En mode édition : chips + champ libre inline. En mode lecture : juste la valeur.
                nodes.potentialRevenue = (
                  <div key="potentialRevenue">
                    <div className="mb-0.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Revenu potentiel ({getSymbol(currency)})</label>
                    </div>
                    {editingContact ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {effectiveBrackets.map(bracket => (
                            <button
                              key={bracket}
                              type="button"
                              onClick={() => {
                                setLocalRevenue(localRevenue === bracket ? '' : bracket);
                                setShowCustomInput(false);
                                setCustomInputValue('');
                              }}
                              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${localRevenue === bracket ? 'orion-revenue-chip-active' : 'orion-revenue-chip-muted'}`}
                            >
                              {bracket}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              if (showCustomInput) {
                                setShowCustomInput(false);
                              } else {
                                setShowCustomInput(true);
                                if (effectiveBrackets.includes(localRevenue)) setLocalRevenue('');
                                setCustomInputValue(isCustomRevenue ? localRevenue : '');
                              }
                            }}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${(showCustomInput || isCustomRevenue) ? 'orion-revenue-chip-active' : 'orion-revenue-chip-muted'}`}
                          >
                            {isCustomRevenue && !showCustomInput ? `✎ ${localRevenue}` : 'Autre…'}
                          </button>
                        </div>
                        {showCustomInput && (
                          <input
                            type="text"
                            value={customInputValue}
                            onChange={e => setCustomInputValue(e.target.value)}
                            onBlur={() => { if (customInputValue.trim()) setLocalRevenue(customInputValue.trim()); }}
                            placeholder={`Montant libre (ex: 75 000 ${getSymbol(currency)})`}
                            className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        )}
                      </div>
                    ) : readOnlyText(lead.potentialRevenue)}
                  </div>
                );
              }

              if (showField('additionalInfo')) {
                nodes.additionalInfo = (
                  <div key="additionalInfo">
                    {editHeader('additionalInfo', 'Notes internes')}
                    {editingContact ? (
                      <textarea
                        value={localAdditionalInfo}
                        onChange={(e) => setLocalAdditionalInfo(e.target.value)}
                        placeholder="Informations complémentaires..."
                        className="w-full h-24 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-2 text-sm resize-none outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    ) : (
                      <p className={`text-sm whitespace-pre-wrap ${lead.additionalInfo ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500 italic'}`}>
                        {lead.additionalInfo || 'Aucune note interne'}
                      </p>
                    )}
                  </div>
                );
              }

              const namedCustomFields = customFieldDefinitions.filter(f => f.name);
              namedCustomFields.forEach(field => {
                  const fkey = `custom:${field.id}`;
                  // Visibilité par champ : chaque custom field a son propre toggle
                  // dans le template (sheetCfg.fields[fkey]). Default = visible.
                  if (showField(fkey as any)) { /* visible */ } else { return; }
                  const value = editingContact
                    ? (localCustomFields[field.id] ?? '')
                    : (lead.customFields?.[field.id] ?? '');
                  // Pendant l'édition, on met à jour uniquement le state local —
                  // le save global (handleSaveAll) persistera l'objet customFields
                  // complet en un seul appel API.
                  const setLocalValue = (newVal: any) => {
                    setLocalCustomFields(prev => ({ ...prev, [field.id]: newVal }));
                  };

                  // Le checkbox doit pouvoir être lu en permanence (pas qu'en édition)
                  if (field.type === 'checkbox') {
                    nodes[fkey] = (
                      <div key={fkey}>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5 block">
                          {field.name}{field.required && <span className="text-rose-400 ml-0.5">*</span>}
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            title={field.name}
                            checked={!!value}
                            disabled={!editingContact}
                            onChange={(e) => setLocalValue(e.target.checked)}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-sm text-slate-600 dark:text-slate-300">{value ? 'Oui' : 'Non'}</span>
                        </label>
                      </div>
                    );
                    return;
                  }

                  const displayValue = field.type === 'date' && value
                    ? new Date(value).toLocaleDateString('fr-FR')
                    : String(value || '');

                  nodes[fkey] = (
                    <div key={fkey}>
                      {editHeader(fkey, field.name + (field.required ? ' *' : ''))}
                      {editingContact ? (
                        <>
                          {field.type === 'text' && (
                            <input type="text" title={field.name} value={value} onChange={(e) => setLocalValue(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                          )}
                          {field.type === 'number' && (
                            <input type="number" title={field.name} value={value} onChange={(e) => setLocalValue(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                          )}
                          {field.type === 'date' && (
                            <input type="date" title={field.name} value={value} onChange={(e) => setLocalValue(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                          )}
                          {field.type === 'select' && (
                            <select title={field.name} value={value} onChange={(e) => setLocalValue(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500">
                              <option value="">— Sélectionner —</option>
                              {(field.options || []).filter(Boolean).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          )}
                        </>
                      ) : readOnlyText(displayValue)}
                    </div>
                  );
                });

              if (showField('appointmentsList')) {
                nodes.appointmentsList = (
                  <div key="appointmentsList">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rendez-vous</label>
                      {showAction('appointment') && (<button type="button" onClick={openNewApptModal} className="text-indigo-600 hover:text-indigo-700 text-xs font-bold">+ Ajouter</button>)}
                    </div>
                    {appointments.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Aucun rendez-vous</p>
                    ) : (
                      <div className="space-y-2">
                        {appointments.slice(0, 3).map(appt => (
                          <div key={appt.id} className="orion-modal-subcard relative group border border-slate-200 dark:border-slate-700 rounded-xl p-3 transition-colors cursor-pointer" onClick={() => openEditApptModal(appt)}>
                            <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{appt.title}</div>
                            <div className="text-[10px] text-slate-400 mt-1">{formatDate(appt.start)}</div>
                            {deleteConfirmApptId === appt.id ? (
                              <div className="absolute top-2 right-2 flex gap-1">
                                <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteAppt(appt.id); }} className="bg-rose-500 text-white px-2 py-1 rounded text-[9px] font-bold">Confirmer</button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setDeleteConfirmApptId(null); }} className="bg-slate-300 text-slate-700 px-2 py-1 rounded text-[9px] font-bold">Annuler</button>
                              </div>
                            ) : (
                              <button type="button"
                                onClick={(e) => { e.stopPropagation(); setDeleteConfirmApptId(appt.id); }}
                                className="absolute top-2 right-2 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              const defaultOrder: string[] = [
                'clientCompanyName', 'email', 'phone', 'phoneLandline',
                'tag', 'sectorActivity', 'assignedTo', 'potentialRevenue', 'additionalInfo',
                ...namedCustomFields.map(f => `custom:${f.id}`),
                'appointmentsList',
              ];
              const contactFieldIds = new Set(['clientCompanyName', 'email', 'phone', 'phoneLandline']);
              const configured = Array.isArray(sheetCfg.fieldOrder) ? sheetCfg.fieldOrder : [];
              const seen = new Set<string>();
              const finalOrder: string[] = [];

              // Legacy : si le template configuré n'a aucun champ contact (sauvé
              // avant l'ajout de cette feature), on prepend les champs contact
              // en haut au lieu de les laisser tomber en queue.
              const hasAnyContactField = configured.some(id => contactFieldIds.has(id));
              if (!hasAnyContactField) {
                defaultOrder.forEach(id => {
                  if (contactFieldIds.has(id) && nodes[id] && !seen.has(id)) {
                    finalOrder.push(id); seen.add(id);
                  }
                });
              }

              configured.forEach(id => { if (nodes[id] && !seen.has(id)) { finalOrder.push(id); seen.add(id); } });
              defaultOrder.forEach(id => { if (nodes[id] && !seen.has(id)) { finalOrder.push(id); seen.add(id); } });
              return <>{finalOrder.map(id => nodes[id])}</>;
            })()}
          </div>

          {/* RIGHT CONTENT — hidden on mobile when Infos tab is active */}
          <div className={`flex-1 flex flex-col overflow-hidden min-w-0 ${activeTab === 'info' ? 'hidden md:flex' : 'flex'}`}>
            {isCreating ? (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-indigo-500/10 text-indigo-400">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-lg font-black text-slate-200">Créez d'abord le prospect</h3>
                <p className="text-sm text-slate-400 max-w-md leading-relaxed">
                  Remplissez les informations à gauche puis cliquez sur la coche verte au-dessus du contact pour créer le prospect.
                  <br />
                  Les onglets Commentaires, Email, Timeline, Opportunités, Tâches et Fichiers seront accessibles ensuite.
                </p>
                <button
                  type="button"
                  onClick={handleSaveAll}
                  className="mt-2 orion-modal-primary-btn text-white font-black px-8 py-3 rounded-2xl text-xs uppercase tracking-widest shadow-xl"
                >
                  Créer le prospect
                </button>
              </div>
            ) : (
            <>
            {/* TABS */}
            <div className="orion-lead-modal-tabs flex-none px-4 md:px-8 pt-4 md:pt-6 pb-3 md:pb-4 flex gap-1 md:gap-2 border-b border-slate-100 dark:border-slate-800 overflow-x-auto scrollbar-hide">
              {(() => {
                const tabDefs: Record<string, { label: string; visible: boolean }> = {
                  comments: { label: '💬 Commentaires', visible: showTab('comments') },
                  email: { label: '📧 Email', visible: showTab('email') },
                  timeline: { label: '📊 Timeline', visible: showTab('timeline') },
                  opportunities: { label: '💼 Opportunités', visible: showTab('opportunities') },
                  tasks: { label: '✅ Tâches', visible: showTab('tasks') },
                  files: { label: '📎 Fichiers', visible: showTab('files') },
                };
                const defaultOrder = ['comments', 'email', 'timeline', 'opportunities', 'tasks', 'files'];
                const configured = Array.isArray(sheetCfg.tabOrder) ? sheetCfg.tabOrder : [];
                const seen = new Set<string>();
                const finalOrder: string[] = [];
                configured.forEach(id => { if (tabDefs[id]?.visible && !seen.has(id)) { finalOrder.push(id); seen.add(id); } });
                defaultOrder.forEach(id => { if (tabDefs[id]?.visible && !seen.has(id)) { finalOrder.push(id); seen.add(id); } });
                return finalOrder.map(id => (
                  <button type="button"
                    key={id}
                    onClick={() => setActiveTab(id as typeof activeTab)}
                    className={`orion-lead-tab px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === id ? 'orion-lead-tab-active text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {tabDefs[id].label}
                  </button>
                ));
              })()}
              {/* Onglet Infos — visible uniquement sur mobile */}
              <button type="button"
                onClick={() => setActiveTab('info')}
                className={`md:hidden orion-lead-tab px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex-shrink-0 ${activeTab === 'info' ? 'orion-lead-tab-active text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
              >
                👤 Infos
              </button>
              {erpCustomerId && showTab('erp') && (
                <button type="button"
                  onClick={() => {
                    setActiveTab('erp' as any);
                    if (!erpDocs && !erpDocsLoading) {
                      setErpDocsLoading(true);
                      const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';
                      const token = sessionStorage.getItem('orion_access_token') || '';
                      fetch(`${API_BASE}?action=erp_documents&lead_id=${lead.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                      })
                        .then(r => r.json())
                        .then(data => { if (data.ok) setErpDocs(data); })
                        .catch(() => {})
                        .finally(() => setErpDocsLoading(false));
                    }
                  }}
                  className={`orion-lead-tab px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${(activeTab as string) === 'erp' ? 'orion-lead-tab-active text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  🏢 ERP
                </button>
              )}
            </div>

            {/* VUE COMMENTAIRES */}
            {activeTab === 'comments' && (
              <>
                {/* Champ d'ajout de commentaire en haut - Pleine largeur */}
                <div className="flex-none px-8 py-6 border-b border-slate-100 dark:border-slate-800">
                  <form onSubmit={handleAddComment} className="relative">
                    <input 
                      type="text"
                      value={newComment} 
                      onChange={(e) => setNewComment(e.target.value)} 
                      placeholder="✍️ Ajouter un commentaire... (utilisez @nom pour mentionner quelqu'un)" 
                      className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-2xl pl-6 pr-14 py-4 text-sm text-slate-800 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 outline-none font-medium" 
                    />
                    <button 
                      type="submit" 
                      disabled={!newComment.trim()} 
                      className="absolute right-2 top-2 p-2 bg-indigo-600 text-white rounded-xl shadow-md hover:bg-indigo-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </form>
                </div>

                {/* Liste des commentaires - Pleine largeur */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Tous les commentaires
                    </h4>
                    <span className="orion-comments-count text-xs font-bold px-3 py-1 rounded-full">
                      {comments.length}
                    </span>
                  </div>

                  {comments.length === 0 ? (
                    <div className="text-center py-16">
                      <svg className="w-20 h-20 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p className="text-slate-400 text-base font-medium">Aucun commentaire pour le moment</p>
                      <p className="text-slate-300 text-sm mt-2">Commencez la conversation en utilisant le champ ci-dessus ☝️</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-w-4xl mx-auto">
                      {comments.map((comment) => (
                        <div key={comment.id} className="orion-modal-subcard p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all">
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                              {comment.userName.charAt(0).toUpperCase()}
                            </div>
                            
                            {/* Contenu */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-slate-800 dark:text-white">{comment.userName}</span>
                                <span className="text-xs text-slate-400">{formatDate(comment.createdAt)}</span>
                              </div>
                              <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                                {comment.content}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* VUE TIMELINE */}
            {activeTab === 'timeline' && (
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-xl font-black text-slate-800 dark:text-white flex items-center gap-2">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Historique des activités
                  </h4>
                  <span className="orion-timeline-count text-xs font-bold text-slate-400 px-4 py-2 rounded-full">
                    {activities.length} événement{activities.length > 1 ? 's' : ''}
                  </span>
                </div>
                
                <Timeline activities={activities} timezone={timezone} />
              </div>
            )}

            {/* VUE OPPORTUNITES */}
            {activeTab === 'opportunities' && (
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="orion-modal-subcard border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
                    <h4 className="text-sm font-black text-slate-800 dark:text-white mb-4">Nouvelle proposition commerciale</h4>
                    <form onSubmit={handleCreateOpportunity} className="space-y-3">
                      <input
                        type="text"
                        value={oppSubject}
                        onChange={(e) => setOppSubject(e.target.value)}
                        placeholder="Objet de la proposition"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold outline-none"
                      />
                      <textarea
                        value={oppComment}
                        onChange={(e) => setOppComment(e.target.value)}
                        placeholder="Commentaire"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium outline-none h-20 resize-none"
                      />

                      <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Produits / services</span>
                          <button type="button" onClick={addOppItemLine} className="text-indigo-600 text-xs font-black">+ Ligne</button>
                        </div>
                        <div className="space-y-2">
                          {oppItems.map((line, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                              <input
                                type="text"
                                value={line.label}
                                onChange={(e) => updateOppItemLine(idx, { label: e.target.value })}
                                placeholder="Produit / service"
                                className="col-span-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-bold"
                              />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.quantity}
                                onChange={(e) => updateOppItemLine(idx, { quantity: Number(e.target.value) })}
                                className="col-span-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-bold"
                              />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.unitPrice}
                                onChange={(e) => updateOppItemLine(idx, { unitPrice: Number(e.target.value) })}
                                className="col-span-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs font-bold"
                              />
                              <div className="col-span-2 text-[11px] font-black text-slate-700 dark:text-slate-300">
                                {(line.quantity * line.unitPrice).toFixed(2)} {getSymbol(currency)}
                              </div>
                              <button type="button" className="col-span-1 text-rose-500 text-xs font-black" onClick={() => removeOppItemLine(idx)} disabled={oppItems.length === 1}>
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="orion-modal-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-50"
                      >
                        {isSubmitting ? 'Création...' : 'Créer opportunité'}
                      </button>
                    </form>
                  </div>

                  <div className="orion-modal-subcard border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-black text-slate-800 dark:text-white">Historique client</h4>
                      <span className="text-xs font-bold text-slate-400">{opportunities.length}</span>
                    </div>
                    {opportunities.length === 0 ? (
                      <p className="text-sm text-slate-400">Aucune opportunité pour ce client.</p>
                    ) : (
                      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                        {opportunities.map((opp) => (
                          <div key={opp.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-black text-slate-800 dark:text-white">{opp.subject}</div>
                              <div className="flex items-center gap-2">
                                <div className="text-xs font-black text-indigo-600">{opp.totalAmount.toFixed(2)} {getSymbol(currency)}</div>
                                <button
                                  type="button"
                                  onClick={() => setPdfOpportunity(opp)}
                                  title="Générer PDF"
                                  className="text-slate-400 hover:text-indigo-400 transition-colors p-1 rounded-lg hover:bg-indigo-500/10"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-1">{new Date(opp.createdAt).toLocaleDateString('fr-FR')}</div>
                            {opp.comment && <div className="text-xs text-slate-500 mt-2">{opp.comment}</div>}
                            <div className="mt-2 space-y-1">
                              {opp.items.map(item => (
                                <div key={item.id} className="text-[11px] text-slate-600 dark:text-slate-300">
                                  {item.label} - {item.quantity} × {item.unitPrice.toFixed(2)} = {item.totalPrice.toFixed(2)} {getSymbol(currency)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="orion-modal-subcard border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
                    <h4 className="text-sm font-black text-slate-800 dark:text-white mb-4">Nouvelle tâche liée au client</h4>
                    <form onSubmit={handleCreateTask} className="space-y-3">
                      <input
                        type="text"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        placeholder="Titre de la tâche"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold outline-none"
                      />
                      <textarea
                        value={taskComment}
                        onChange={(e) => setTaskComment(e.target.value)}
                        placeholder="Commentaire"
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium outline-none h-24 resize-none"
                      />
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Échéance</label>
                        <input
                          type="datetime-local"
                          value={taskDueAt}
                          onChange={(e) => setTaskDueAt(e.target.value)}
                          className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold outline-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="orion-modal-primary-btn text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-50"
                      >
                        {isSubmitting ? 'Création...' : 'Créer tâche'}
                      </button>
                    </form>
                  </div>

                  <div className="orion-modal-subcard border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-black text-slate-800 dark:text-white">Tâches du client</h4>
                      <span className="text-xs font-bold text-slate-400">{tasks.length}</span>
                    </div>
                    {tasks.length === 0 ? (
                      <p className="text-sm text-slate-400">Aucune tâche pour ce client.</p>
                    ) : (
                      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                        {tasks.map(task => (
                          <div key={task.id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-bold text-sm text-slate-800 dark:text-white">{task.title}</div>
                              <span className={`px-2 py-1 rounded text-[10px] font-black ${task.status === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {task.status === 'done' ? 'Validée' : 'À faire'}
                              </span>
                            </div>
                            {task.comment && <div className="text-xs text-slate-500 mt-1">{task.comment}</div>}
                            <div className="text-[11px] text-slate-400 mt-2">
                              Échéance: {new Date(task.dueAt).toLocaleString('fr-FR')}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <button type="button" onClick={() => toggleTaskDone(task)} className="px-3 py-1.5 rounded-lg text-[10px] font-black bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                                {task.status === 'done' ? 'Réouvrir' : 'Valider'}
                              </button>
                              <button type="button" onClick={() => editTask(task)} className="px-3 py-1.5 rounded-lg text-[10px] font-black bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                                Modifier
                              </button>
                              {confirmDeleteTaskInModal === task.id ? (
                                <div className="flex gap-1 ml-auto">
                                  <button type="button" onClick={() => handleDeleteTaskInModal(task.id)} className="px-2 py-1.5 rounded-lg text-[10px] font-black bg-rose-500 text-white">Oui</button>
                                  <button type="button" onClick={() => setConfirmDeleteTaskInModal(null)} className="px-2 py-1.5 rounded-lg text-[10px] font-black bg-slate-200 text-slate-600">Non</button>
                                </div>
                              ) : (
                                <button type="button" onClick={() => setConfirmDeleteTaskInModal(task.id)} className="ml-auto px-2 py-1.5 rounded-lg text-[10px] font-black bg-rose-50 text-rose-600 hover:bg-rose-100" title="Supprimer">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* VUE FICHIERS */}
            {activeTab === 'files' && (
              <div className="p-8 h-full flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-sm font-black text-slate-800 dark:text-white mb-1">Pieces jointes</h4>
                    <p className="text-xs text-slate-500">{attachments.length} fichier{attachments.length !== 1 ? 's' : ''}</p>
                  </div>
                  <label className="orion-modal-primary-btn text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wide cursor-pointer flex items-center gap-2">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    {isUploading ? 'Envoi...' : 'Ajouter'}
                    <input
                      type="file"
                      className="hidden"
                      disabled={isUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setIsUploading(true);
                        try {
                          await api.uploadAttachment(lead.id, file);
                          const atts = await api.getAttachments(lead.id);
                          setAttachments(atts);
                          showToast('Fichier ajoute', 'success');
                        } catch (err: any) {
                          showToast(err.message || 'Erreur upload', 'info');
                        } finally {
                          setIsUploading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>

                {attachments.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-12 h-12 mx-auto text-slate-200 dark:text-slate-700 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                      <p className="text-xs text-slate-400 font-bold">Aucune piece jointe</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-3">
                    {attachments.map((att: any) => {
                      const isImage = att.mime_type?.startsWith('image/');
                      const isPdf = att.mime_type === 'application/pdf';
                      const sizeKb = Math.round((att.file_size || 0) / 1024);
                      const sizeLabel = sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)} Mo` : `${sizeKb} Ko`;
                      return (
                        <div key={att.id} className="orion-modal-subcard flex items-center gap-4 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isImage ? 'bg-violet-500/10' : isPdf ? 'bg-rose-500/10' : 'bg-indigo-500/10'}`}>
                            {isImage ? (
                              <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            ) : isPdf ? (
                              <svg className="w-5 h-5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            ) : (
                              <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{att.original_name}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{sizeLabel} · {att.user_name || 'Inconnu'} · {new Date(att.created_at).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={async () => {
                                try { await api.downloadAttachment(att.id); }
                                catch { toast.error('Erreur lors du téléchargement'); }
                              }}
                              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-indigo-400 transition-colors"
                              title="Telecharger"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm('Supprimer ce fichier ?')) return;
                                try {
                                  await api.deleteAttachment(att.id);
                                  setAttachments(prev => prev.filter(a => a.id !== att.id));
                                  showToast('Fichier supprime', 'success');
                                } catch { showToast('Erreur suppression', 'info'); }
                              }}
                              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-rose-900/30 text-slate-400 hover:text-rose-400 transition-colors"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* VUE ERP DOCUMENTS */}
            {(activeTab as string) === 'erp' && (
              <div className="p-8 h-full flex flex-col overflow-y-auto">
                <div className="mb-6">
                  <h4 className="text-sm font-black text-slate-800 dark:text-white mb-1">📄 Documents ERP</h4>
                  <p className="text-xs text-slate-500">Devis, factures et paiements liés dans l'ERP</p>
                </div>

                {erpDocsLoading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-xs text-slate-400 font-bold">Chargement depuis l'ERP...</p>
                    </div>
                  </div>
                ) : !erpDocs || !erpDocs.linked ? (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-xs text-slate-400">Aucune donnée disponible</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* DEVIS */}
                    <div>
                      <h5 className="text-xs font-black uppercase tracking-wider text-indigo-400 mb-3">📋 Devis ({erpDocs.quotes.length})</h5>
                      {erpDocs.quotes.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">Aucun devis</p>
                      ) : (
                        <div className="space-y-2">
                          {erpDocs.quotes.map((q: any) => (
                            <div key={q.id} className="orion-modal-subcard p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                              <div>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">{q.reference || `Devis #${q.id}`}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{q.title || q.status} · {new Date(q.created_at).toLocaleDateString('fr-FR')}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-black text-emerald-500">{Number(q.total_ttc || q.total_ht || 0).toLocaleString('fr-FR', { style: 'currency', currency: currency || 'EUR' })}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                                  q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                  q.status === 'sent' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                }`}>{q.status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* FACTURES */}
                    <div>
                      <h5 className="text-xs font-black uppercase tracking-wider text-rose-400 mb-3">🧾 Factures ({erpDocs.invoices.length})</h5>
                      {erpDocs.invoices.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">Aucune facture</p>
                      ) : (
                        <div className="space-y-2">
                          {erpDocs.invoices.map((inv: any) => (
                            <div key={inv.id} className="orion-modal-subcard p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                              <div>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">{inv.reference || `Facture #${inv.id}`}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">Échéance: {inv.due_date ? new Date(inv.due_date).toLocaleDateString('fr-FR') : 'N/A'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-black text-rose-500">{Number(inv.total_ttc || inv.total_ht || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                                  inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                  inv.status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                  inv.status === 'sent' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                }`}>{inv.status}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* PAIEMENTS */}
                    <div>
                      <h5 className="text-xs font-black uppercase tracking-wider text-amber-400 mb-3">💰 Paiements ({erpDocs.payments.length})</h5>
                      {erpDocs.payments.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">Aucun paiement</p>
                      ) : (
                        <div className="space-y-2">
                          {erpDocs.payments.map((p: any) => (
                            <div key={p.id} className="orion-modal-subcard p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                              <div>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">{p.invoice_ref || `Paiement #${p.id}`}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{p.method} · {new Date(p.payment_date).toLocaleDateString('fr-FR')}</p>
                              </div>
                              <p className="text-sm font-black text-amber-500">{Number(p.amount || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VUE EMAIL */}
            {activeTab === 'email' && (
              <div className="p-8 h-full flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-sm font-black text-slate-800 dark:text-white mb-1">Envoyer un Email</h4>
                    <p className="text-xs text-slate-500">Le message sera envoyé et archivé dans la timeline.</p>
                  </div>
                  <SelectField
                    value=""
                    onChange={(v) => { const tpl = emailTemplates.find(t => t.id === v); if (tpl) handleApplyTemplate(tpl); }}
                    options={[
                      { value: '', label: 'Utiliser un template...' },
                      ...emailTemplates.map(t => ({ value: t.id, label: t.name }))
                    ]}
                    className="text-xs font-bold py-2 px-4 rounded-xl"
                  />
                </div>

                <form onSubmit={handleSendEmail} className="flex-1 flex flex-col space-y-4">
                  <div className="relative">
                    <input
                      ref={emailSubjectRef}
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      onFocus={() => setEmailActiveField('subject')}
                      placeholder="Objet de l'email"
                      className="w-full bg-transparent border-b border-slate-200 dark:border-slate-800 px-2 py-3 text-sm font-bold text-slate-800 dark:text-white placeholder:text-slate-300 outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>

                  {/* Palette de variables */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowVariablePalette(v => !v)}
                      className="orion-lead-select flex items-center gap-1.5 text-[10px] font-bold py-1.5 px-3 rounded-xl transition-colors"
                      title="Insérer une variable dynamique"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
                      Insérer une variable
                      <span className="text-[9px] opacity-60">→ {emailActiveField === 'subject' ? 'sujet' : 'corps'}</span>
                    </button>
                    {showVariablePalette && (
                      <div className="absolute z-50 top-full left-0 mt-1 w-[28rem] max-h-80 overflow-y-auto bg-[#0e1c3a] border border-[rgba(100,140,210,0.3)] rounded-xl shadow-2xl p-3 space-y-3">
                        {(['lead', 'user', 'company', 'date'] as const).map(group => {
                          const vars = EMAIL_TEMPLATE_VARIABLES.filter(v => v.group === group);
                          if (vars.length === 0) return null;
                          return (
                            <div key={group}>
                              <div className="text-[9px] font-black uppercase tracking-widest text-indigo-400 mb-1.5">{emailVariableGroupLabel(group)}</div>
                              <div className="flex flex-wrap gap-1">
                                {vars.map(v => (
                                  <button
                                    key={v.key}
                                    type="button"
                                    title={v.label}
                                    onClick={() => {
                                      if (emailActiveField === 'subject') {
                                        const el = emailSubjectRef.current;
                                        const { value, cursor } = insertVariableAtCursor(emailSubject, v.token, el?.selectionStart ?? null, el?.selectionEnd ?? null);
                                        setEmailSubject(value);
                                        requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(cursor, cursor); } });
                                      } else {
                                        const el = emailBodyRef.current;
                                        const { value, cursor } = insertVariableAtCursor(emailBody, v.token, el?.selectionStart ?? null, el?.selectionEnd ?? null);
                                        setEmailBody(value);
                                        requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(cursor, cursor); } });
                                      }
                                    }}
                                    className="text-[10px] font-bold py-1 px-2 rounded-md bg-[rgba(100,140,210,0.1)] text-[#a6b7e0] hover:bg-indigo-500/20 hover:text-white transition-colors"
                                  >
                                    {v.token}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <textarea
                    ref={emailBodyRef}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    onFocus={() => setEmailActiveField('body')}
                    placeholder="Rédigez votre message ici... Insérez des variables dynamiques avec le bouton ci-dessus."
                    className="flex-1 w-full bg-slate-50 dark:bg-slate-950 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 text-sm text-slate-600 dark:text-slate-300 leading-relaxed outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
                  />
                  {/* Pièces jointes */}
                  <div className="flex flex-wrap items-center gap-2">
                    <input id="email-pj-input" type="file" multiple style={{ display: 'none' }} title="Ajouter des pièces jointes" onChange={(e) => {
                      const files = e.target.files;
                      console.log('[PJ] onChange déclenché, fichiers:', files?.length);
                      if (files && files.length > 0) {
                        setEmailAttachments(prev => [...prev, ...Array.from(files)]);
                      }
                      e.target.value = '';
                    }} />
                    <button type="button" onClick={() => { console.log('[PJ] Clic bouton PC'); const inp = document.getElementById('email-pj-input') as HTMLInputElement; if (inp) inp.click(); else console.error('[PJ] Input introuvable!'); }} className="orion-lead-select flex items-center gap-1.5 text-[10px] font-bold py-1.5 px-3 rounded-xl transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                      Depuis mon PC
                    </button>
                    {attachments.length > 0 && (
                      <div className="relative">
                        <button type="button" onClick={() => setShowLeadFilePicker(v => !v)} className="orion-lead-select flex items-center gap-1.5 text-[10px] font-bold py-1.5 px-3 rounded-xl transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" /></svg>
                          Fichiers du lead ({attachments.length})
                        </button>
                        {showLeadFilePicker && (
                        <div className="absolute bottom-full left-0 mb-1 w-64 bg-[#0e1c3a] border border-[rgba(100,140,210,0.3)] rounded-xl shadow-2xl p-2 z-50 max-h-48 overflow-y-auto">
                          {attachments.map((att: any) => {
                            const selected = emailLeadFiles.includes(String(att.id));
                            return (
                              <button key={att.id} type="button" onClick={() => { const sid = String(att.id); setEmailLeadFiles(prev => selected ? prev.filter(id => id !== sid) : [...prev, sid]); }}
                                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors ${selected ? 'bg-cyan-500/20 text-cyan-300' : 'text-[#7f93bc] hover:bg-[rgba(100,140,210,0.1)]'}`}>
                                {selected ? <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : <span className="w-3 h-3 flex-shrink-0 rounded border border-[rgba(100,140,210,0.3)]"></span>}
                                <span className="truncate">{att.original_name}</span>
                              </button>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    )}
                    {/* Liste des PJ sélectionnées */}
                    {(emailAttachments.length > 0 || emailLeadFiles.length > 0) && (
                      <>
                        {emailAttachments.map((f, i) => (
                          <span key={`pc-${i}`} className="flex items-center gap-1 bg-indigo-500/15 text-indigo-300 text-[10px] font-bold px-2.5 py-1 rounded-lg">
                            📎 {f.name}
                            <button type="button" onClick={() => setEmailAttachments(prev => prev.filter((_, j) => j !== i))} className="hover:text-rose-400 ml-0.5">×</button>
                          </span>
                        ))}
                        {emailLeadFiles.map(id => {
                          const att = attachments.find((a: any) => String(a.id) === id);
                          return att ? (
                            <span key={`lead-${id}`} className="flex items-center gap-1 bg-cyan-500/15 text-cyan-300 text-[10px] font-bold px-2.5 py-1 rounded-lg">
                              📁 {att.original_name}
                              <button type="button" onClick={() => setEmailLeadFiles(prev => prev.filter(x => x !== id))} className="hover:text-rose-400 ml-0.5">×</button>
                            </span>
                          ) : null;
                        })}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      {!showSaveTemplate ? (
                        <button type="button" onClick={() => setShowSaveTemplate(true)} disabled={!emailBody.trim()} className="orion-lead-select flex items-center gap-1.5 text-[11px] font-bold py-1.5 px-3 rounded-xl transition-colors disabled:opacity-30">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                          Sauver template
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Nom du template" className="orion-lead-select text-xs px-3 py-1.5 rounded-xl w-40" />
                          <button type="button" onClick={handleSaveTemplate} disabled={!templateName.trim()} className="orion-lead-select text-[11px] font-bold py-1.5 px-3 rounded-xl text-emerald-400 disabled:opacity-30">Sauver</button>
                          <button type="button" onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }} className="orion-lead-select text-[11px] font-bold py-1.5 px-3 rounded-xl text-slate-400">Annuler</button>
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting || !emailBody}
                      className="orion-modal-primary-btn text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSubmitting ? 'Envoi...' : (
                        <>
                          <span>Envoyer</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Liste des templates existants */}
                {emailTemplates.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-white/5">
                    <h5 className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Templates sauvegardés</h5>
                    <div className="flex flex-wrap gap-2">
                      {emailTemplates.map(tpl => (
                        <div key={tpl.id} className="group orion-lead-select flex items-center gap-2 rounded-xl px-3 py-2 cursor-pointer" onClick={() => handleApplyTemplate(tpl)}>
                          <svg className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="text-xs font-bold group-hover:text-[#11c5d6] transition-colors">{tpl.name}</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tpl.id); }} className="text-[#3d5280] hover:text-rose-400 transition-colors opacity-0 group-hover:opacity-100 ml-1" title="Supprimer">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* MODAL ENREGISTREMENT APPEL */}
      {showCallLog && (
        <div className="orion-modal-backdrop fixed inset-0 z-[110] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel orion-appointment-modal w-full max-w-sm rounded-[2rem] shadow-2xl p-8 border animate-in zoom-in-95">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-1.88.94a11.042 11.042 0 005.329 5.329l.94-1.88a1 1 0 011.21-.502l4.493 1.498A1 1 0 0121 16.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </div>
              <div>
                <h3 className="text-lg font-black dark:text-white">Enregistrer un appel</h3>
                <p className="text-xs text-slate-400">{lead.name} · {displayPhone(lead.phone || '', currentUser.role, maskContacts) || 'Sans numero'}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Direction</label>
                  <SelectField
                    value={callDirection}
                    onChange={(val) => setCallDirection(val as 'inbound' | 'outbound')}
                    options={[
                      { value: 'outbound', label: 'Sortant' },
                      { value: 'inbound', label: 'Entrant' },
                    ]}
                    className="w-full rounded-xl px-3 py-3 text-sm font-bold"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Duree (sec)</label>
                  <input
                    type="number"
                    placeholder="120"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                    min={1}
                    className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-3 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Notes</label>
                <textarea
                  placeholder="Resume de l'appel..."
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium outline-none h-20 resize-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowCallLog(false)} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-slate-600">Annuler</button>
                <button
                  type="button"
                  onClick={handleLogCall}
                  disabled={!callDuration || parseInt(callDuration) <= 0}
                  className="orion-modal-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold shadow-lg disabled:opacity-50"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showApptModal && (
        <div className="orion-modal-backdrop fixed inset-0 z-[110] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel orion-appointment-modal w-full max-w-md rounded-[2rem] shadow-2xl p-8 border animate-in zoom-in-95">
            <h3 className="text-xl font-black mb-6 dark:text-white">{editingApptId ? 'Modifier Rendez-vous' : 'Nouveau Rendez-vous'}</h3>
            <form onSubmit={handleSaveAppt} className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Titre</label>
                <input type="text" value={apptForm.title} onChange={e => setApptForm({...apptForm, title: e.target.value})} placeholder="Objet du RDV" className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Date</label>
                  <input type="date" value={apptForm.date} onChange={e => setApptForm({...apptForm, date: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-3 py-3 text-sm font-bold outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Type</label>
                  <SelectField
                    value={apptForm.type}
                    onChange={val => setApptForm({...apptForm, type: val as AppointmentType})}
                    options={[
                      { value: AppointmentType.PHONE, label: 'Téléphone' },
                      { value: AppointmentType.VIDEO, label: 'Visio' },
                      { value: AppointmentType.PHYSICAL, label: 'Physique' },
                    ]}
                    className="w-full rounded-xl px-3 py-3 text-sm font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Début</label>
                  <input type="time" value={apptForm.startTime} onChange={e => setApptForm({...apptForm, startTime: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-3 py-3 text-sm font-bold outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Fin</label>
                  <input type="time" value={apptForm.endTime} onChange={e => setApptForm({...apptForm, endTime: e.target.value})} className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-3 py-3 text-sm font-bold outline-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Description</label>
                <textarea 
                  value={apptForm.description} 
                  onChange={e => setApptForm({...apptForm, description: e.target.value})} 
                  placeholder="Détails, lien visio, code porte..." 
                  className="w-full bg-slate-50 dark:bg-slate-950 border dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium outline-none h-24 resize-none" 
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowApptModal(false)} disabled={isSavingAppt} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-slate-600 disabled:opacity-50">Annuler</button>
                <button type="submit" disabled={isSavingAppt} className="orion-modal-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSavingAppt ? 'Enregistrement…' : (editingApptId ? 'Enregistrer' : 'Créer RDV')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {pdfOpportunity && (
        <OpportunityPDFModal
          opportunity={pdfOpportunity}
          companyInfo={{ currency }}
          onClose={() => setPdfOpportunity(null)}
        />
      )}
    </>
  );
};

export default LeadDetailsModal;
